import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../button/button';
import '../icon/icon';

type WidthSize = 'sm' | 'md' | 'lg' | 'xl';
type HeightSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Modal component
 */
@customElement('luds-modal')
export class LudsModal extends LitElement {
  static override styles = [
    resetStyle,
    ludsBaseFont,
    overrideDesignTokens,
    css`
      :host(.luds-modal-fixed) {
        position: fixed;
        inset: 0;
      }

      dialog {
        padding: 0;
        inset: 0;
        border: 1px solid rgba(255, 255, 255, 0);
        border-radius: 8px;
        box-shadow: 3px 4px 10px 0px rgba(0, 0, 0, 0.2);
      }

      .dialog-content {
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
      }

      .dialog-header {
        padding: var(--luds-dialog-header-spacing, 1rem);
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #ddd;
      }

      .dialog-footer {
        padding: var(--luds-dialog-footer-spacing, 1rem);
        border-top: 1px solid #ddd;
      }

      .no-border {
        border: none;
        display: none;
      }

      .dialog-title {
        margin: 0;
        font-size: 1.8rem;
        font-weight: 500;
      }

      .dialog-header-actions {
        display: flex;
        align-items: center;
        margin-left: auto;
        margin-right: 24px;
      }

      .dialog-body {
        padding: var(--luds-dialog-body-spacing, 1rem);
        flex-grow: 1;
        overflow: auto;
      }

      .dialog-close {
        position: absolute;
        z-index: 1;
        right: 0;
        padding: var(--spacing-spacing-03);
      }

      .resizable {
        resize: both;
        overflow: auto;
      }

      @keyframes showDialogAnimation {
        0% {
          transform: translateY(100%);
          opacity: 0;
        }
        100% {
          transform: translateY(0%);
          opacity: 1;
        }
      }

      .show-dialog-animation {
        animation: 1s ease-in-out showDialogAnimation;
      }

      @media (max-width: 840px) {
        .width-sm,
        .width-md,
        .width-lg,
        .width-xl {
          width: calc(100% - var(--spacing-spacing-24));
        }
      }

      @media (min-width: 841px) and (max-width: 1024px) {
        .width-sm {
          width: 50%;
        }
        .width-md {
          width: 75%;
        }
        .width-lg,
        .width-xl {
          width: 100%;
        }
      }

      @media (min-width: 1025px) and (max-width: 1280px) {
        .width-sm {
          width: 42%;
        }
        .width-md {
          width: 50%;
        }
        .width-lg {
          width: 83%;
        }
        .width-xl {
          width: 100%;
        }
      }

      @media (min-width: 1281px) {
        .width-sm {
          width: 42%;
        }
        .width-md {
          width: 50%;
        }
        .width-lg {
          width: 83%;
        }
        .width-xl {
          width: 100%;
        }
      }

      .height-sm {
        height: 27%;
      }
      .height-md {
        height: 64%;
      }
      .height-lg {
        height: 83%;
      }
      .height-xl {
        height: 100%;
      }
    `,
  ];

  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  override title = '';

  @property({ type: Boolean })
  animation = false;

  @property({ type: Boolean })
  resizable = false;

  @property({ type: Boolean })
  showCloseIcon = true;

  @property({ type: String })
  width: WidthSize | number = 'md';

  @property({ type: String })
  height: HeightSize | number = 'md';

  @property({ type: Boolean })
  showCustomHeader = false;

  @property({ type: Boolean })
  showCustomFooter = false;

  dialogRef: Ref<HTMLDialogElement> = createRef();

  private initialWidth = '';
  private initialHeight = '';

  override firstUpdated() {
    this.setInitialModalDimensions();
  }

  close() {
    const event = new CustomEvent('luds-dialog-close', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: this.dialogRef.value,
    });

    this.dispatchEvent(event);

    if (!event.defaultPrevented) {
      this.open = false;
      this.dialogRef.value.close();
    }
  }

  show(showModal?: boolean) {
    if (showModal) {
      this.dialogRef.value?.showModal();
    } else {
      this.dialogRef.value?.show();
    }

    this.dispatchEvent(
      new CustomEvent('luds-dialog-show', {
        bubbles: true,
        composed: true,
        detail: this.dialogRef.value,
      })
    );
  }

  toggleFullScreen() {
    const fullScreenWidth = '100vw';
    const fullScreenHeight = '100vh';

    const dialog = this.dialogRef.value;
    const { width, height } = dialog.style;

    const isFullScreen = width === fullScreenWidth && height === fullScreenHeight;

    if (isFullScreen) {
      dialog.style.width = this.initialWidth;
      dialog.style.height = this.initialHeight;
    } else {
      this.initialWidth = width;
      this.initialHeight = height;
      dialog.style.width = fullScreenWidth;
      dialog.style.height = fullScreenHeight;
    }
  }

  private setModalWidth(dialog: HTMLDialogElement): void {
    if (this.width === 'sm' || this.width === 'md' || this.width === 'lg' || this.width === 'xl') {
      dialog.classList.add(`width-${this.width}`);
    } else {
      dialog.style.width = `${this.width}px`;
      // eslint-disable-next-line no-console
      console.warn(
        `Modal width value: ${this.width} is not aligned with luds design system. Expected values are 'sm', 'md', 'lg' and 'xl'`
      );
    }
  }

  private setModalHeight(dialog: HTMLDialogElement): void {
    if (this.height === 'sm' || this.height === 'md' || this.height === 'lg' || this.height === 'xl') {
      dialog.classList.add(`height-${this.height}`);
    } else {
      dialog.style.height = `${this.height}px`;
      // eslint-disable-next-line no-console
      console.warn(
        `Modal height value: ${this.height} is not aligned with luds design system. Expected values are 'sm', 'md', 'lg' and 'xl'`
      );
    }
  }

  private setInitialModalDimensions(): void {
    const dialog = this.dialogRef.value;

    dialog.classList.remove(
      'width-sm',
      'width-md',
      'width-lg',
      'width-xl',
      'height-sm',
      'height-md',
      'height-lg',
      'height-xl'
    );
    this.setModalWidth(dialog);
    this.setModalHeight(dialog);
  }

  private renderCustomDialogHeader() {
    return html`<slot name="custom-dialog-header"></slot>`;
  }

  private renderDialogHeader() {
    return html`<div class="dialog-header">
      <h2 class="dialog-title">${this.title}</h2>
      <div class="dialog-header-actions">
        <slot name="header-actions"></slot>
      </div>
    </div>`;
  }

  private renderCustomDialogFooter() {
    return html`<slot name="custom-dialog-footer"></slot>`;
  }

  private renderDialogFooter() {
    return html`<div class="dialog-footer">
      <slot name="footer"></slot>
    </div>`;
  }

  override render() {
    return html`
      <dialog
        ?open=${this.open}
        ${ref(this.dialogRef)}
        class="${this.animation ? 'show-dialog-animation' : ''} ${this.resizable ? 'resizable' : ''}">
        ${this.showCloseIcon
          ? html`<luds-button
              variant="ghost"
              size="small"
              icon="close"
              @click="${this.close}"
              class="dialog-close"
              data-qa-id="modalClose"></luds-button>`
          : nothing}
        <div class="dialog-content">
          ${this.showCustomHeader ? this.renderCustomDialogHeader() : this.renderDialogHeader()}
          <div class="dialog-body">
            <slot></slot>
          </div>
          ${this.showCustomFooter ? this.renderCustomDialogFooter() : this.renderDialogFooter()}
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-modal': LudsModal;
  }
}
